/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import {
	ApiError,
	EmployeeRegistrationPasswordStrengthEvaluationRequestDTO,
	EmployeeRegistrationService,
	EnterPasswordForEmployeeRegistrationDTO,
	EnterPhoneVerificationCodeForEmployeeRegistrationDTO
} from '../../generated/iam-client';
import {from, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {
	iamApiEmployeeRegistrationEnterPasswordNotFound,
	iamApiEmployeeRegistrationEnterPasswordUnexpectedError,
	iamApiEmployeeRegistrationEnterPasswordValidationException,
	iamApiEmployeeRegistrationEnterPhoneVerificationCodeNotFound,
	iamApiEmployeeRegistrationEnterPhoneVerificationCodeUnexpectedError,
	iamApiEmployeeRegistrationEnterPhoneVerificationCodeValidationException,
	iamApiEmployeeRegistrationEvaluatePasswordStrengthNotFound,
	iamApiEmployeeRegistrationEvaluatePasswordStrengthUnexpectedError,
	iamApiEmployeeRegistrationPasswordEntered,
	iamApiEmployeeRegistrationPasswordStrengthEvaluated,
	iamApiEmployeeRegistrationPhoneVerificationCodeEntered,
	iamApiEmployeeRegistrationRestartPhoneVerificationNotFound,
	iamApiEmployeeRegistrationRestartPhoneVerificationRequested,
	iamApiEmployeeRegistrationRestartPhoneVerificationUnexpectedError,
	iamApiEmployeeRegistrationRetryPhoneVerificationNotFound,
	iamApiEmployeeRegistrationRetryPhoneVerificationRequested,
	iamApiEmployeeRegistrationRetryPhoneVerificationUnexpectedError,
	iamApiEmployeeRegistrationStateRetrieved,
	iamApiRetrieveEmployeeRegistrationStateNotFound,
	iamApiRetrieveEmployeeRegistrationStateUnexpectedError
} from './actions';
import {AnyAction} from 'redux';

class EmployeeRegistrationResource {

	employeeRegistrationState(employeeRegistrationId: string): Observable<AnyAction> {
		const promise = EmployeeRegistrationService.employeeRegistrationState(employeeRegistrationId);
		return from(promise)
			.pipe(
				map((responseBody) => iamApiEmployeeRegistrationStateRetrieved({state: responseBody})),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiRetrieveEmployeeRegistrationStateNotFound());
						default:
							return of(iamApiRetrieveEmployeeRegistrationStateUnexpectedError());

					}
				})
			);
	}

	retryPhoneVerification(employeeRegistrationId: string): Observable<AnyAction> {
		const promise = EmployeeRegistrationService.retryPhoneVerificationForEmployeeRegistration(employeeRegistrationId);
		return from(promise)
			.pipe(
				map(() => iamApiEmployeeRegistrationRetryPhoneVerificationRequested()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiEmployeeRegistrationRetryPhoneVerificationNotFound());
						default:
							return of(iamApiEmployeeRegistrationRetryPhoneVerificationUnexpectedError());
					}
				})
			);
	}

	restartPhoneVerification(employeeRegistrationId: string): Observable<AnyAction> {
		const promise = EmployeeRegistrationService.restartPhoneVerificationForEmployeeRegistration(employeeRegistrationId);
		return from(promise)
			.pipe(
				map(() => iamApiEmployeeRegistrationRestartPhoneVerificationRequested()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiEmployeeRegistrationRestartPhoneVerificationNotFound());
						default:
							return of(iamApiEmployeeRegistrationRestartPhoneVerificationUnexpectedError());
					}
				})
			);
	}

	enterPhoneVerificationCode(
		employeeRegistrationId: string, command: EnterPhoneVerificationCodeForEmployeeRegistrationDTO
	): Observable<AnyAction> {
		const promise = EmployeeRegistrationService.enterPhoneVerificationCodeForEmployeeRegistration(employeeRegistrationId, command);
		return from(promise)
			.pipe(
				map(() => iamApiEmployeeRegistrationPhoneVerificationCodeEntered()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiEmployeeRegistrationEnterPhoneVerificationCodeNotFound());
						case 422:
							return of(iamApiEmployeeRegistrationEnterPhoneVerificationCodeValidationException(error.body));
						default:
							return of(iamApiEmployeeRegistrationEnterPhoneVerificationCodeUnexpectedError());
					}
				})
			);
	}

	evaluatedPasswordStrength(
		employeeRegistrationId: string, evaluationRequest: EmployeeRegistrationPasswordStrengthEvaluationRequestDTO
	): Observable<AnyAction> {
		const promise =
			EmployeeRegistrationService.evaluatedPasswordStrengthForEmployeeRegistration(employeeRegistrationId, evaluationRequest);
		return from(promise)
			.pipe(
				map((responseBody) => iamApiEmployeeRegistrationPasswordStrengthEvaluated({passwordStrength: responseBody})),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiEmployeeRegistrationEvaluatePasswordStrengthNotFound());
						default:
							return of(iamApiEmployeeRegistrationEvaluatePasswordStrengthUnexpectedError());
					}
				})
			);
	}

	enterPassword(employeeRegistrationId: string, command: EnterPasswordForEmployeeRegistrationDTO): Observable<AnyAction> {
		const promise = EmployeeRegistrationService.enterPasswordForEmployeeRegistration(employeeRegistrationId, command);
		return from(promise)
			.pipe(
				map(() => iamApiEmployeeRegistrationPasswordEntered()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiEmployeeRegistrationEnterPasswordNotFound());
						case 422:
							return of(iamApiEmployeeRegistrationEnterPasswordValidationException(error.body));
						default:
							return of(iamApiEmployeeRegistrationEnterPasswordUnexpectedError());
					}
				})
			);
	}
}

export default EmployeeRegistrationResource;
